/**
 * Time-input helpers for the as-of family of controls.
 *
 * Three functions:
 *
 *   * `naiveLocalToIsoOffset` -- convert <input type="datetime-local">
 *     output ("YYYY-MM-DDTHH:MM" naive) to an ISO 8601 string with
 *     the browser's current timezone offset. The chain READ + DIFF
 *     endpoints reject naive datetimes at 422; this helper closes
 *     that gap so the URL + wire format is always tz-aware.
 *
 *   * `formatIsoForDisplay` -- render an ISO 8601 string back as a
 *     human-friendly localised string. Used by every "viewing as of"
 *     surface so the same timestamp reads the same across pickers.
 *
 *   * `dateToNaiveLocalInput` -- inverse helper that converts a JS
 *     Date to the naive shape <input type="datetime-local"> accepts
 *     as `value`. Lets a component pre-fill "30 days ago" defaults
 *     for the diff panel.
 *
 * Ported wholesale from v1 (Marsad_Platform/frontend/src/components/
 * _shared/timeInputs.ts). The functions are stable + well-tested
 * in v1; v2 inherits them verbatim.
 */

import { localeFor } from "shared/time/formatDate";

/**
 * Convert a `<input type="datetime-local">` value (naive
 * "YYYY-MM-DDTHH:MM") to an ISO 8601 string with the browser's
 * current timezone offset.
 *
 * Returns null when the input is empty or unparseable -- callers
 * treat null as "no value provided" (apply button stays disabled,
 * URL update doesn't fire).
 */
export function naiveLocalToIsoOffset(naive: string): string | null {
  if (!naive) return null;
  // `new Date("2026-03-31T17:00")` interprets the string as local
  // browser time -- exactly what the customer typed.
  const parsed = new Date(naive);
  if (Number.isNaN(parsed.getTime())) return null;
  // toISOString() emits UTC ("Z"); we want the browser's offset
  // preserved so the URL is faithful to what the customer picked.
  // Build the offset suffix manually.
  const tzMin = parsed.getTimezoneOffset(); // minutes WEST of UTC
  const offsetHours = Math.abs(Math.trunc(tzMin / 60));
  const offsetMins = Math.abs(tzMin % 60);
  const sign = tzMin <= 0 ? "+" : "-";
  const pad = (n: number) => n.toString().padStart(2, "0");
  const offsetStr = `${sign}${pad(offsetHours)}:${pad(offsetMins)}`;
  const y = parsed.getFullYear();
  const mo = pad(parsed.getMonth() + 1);
  const d = pad(parsed.getDate());
  const h = pad(parsed.getHours());
  const mi = pad(parsed.getMinutes());
  const s = pad(parsed.getSeconds());
  return `${y}-${mo}-${d}T${h}:${mi}:${s}${offsetStr}`;
}

/** Human-friendly localised display of an as-of ISO string.
 *
 * F -- `undefined` as the locale means "whatever this machine is set
 * to", which is precisely what F3 removed everywhere else: an as-of
 * banner read 8/30/2026 on a browser configured for the United States,
 * which is 8 March to most of the people who will read it. The reader's
 * chosen language decides, as it does for every other date here.
 */
export function formatIsoForDisplay(iso: string, language?: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(localeFor(language), {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return iso;
  }
}

/**
 * Convert a JS Date to the naive "YYYY-MM-DDTHH:MM" shape the
 * `<input type="datetime-local">` element accepts as `value`.
 *
 * Lets the ChainDiffPanel (P2.8.d) pre-fill its From input with
 * "30 days ago" and To with "now" -- one-click working comparison.
 */
export function dateToNaiveLocalInput(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
